/**
 * 面談の効果をまとめて比べる(2026-10-09 発注者指示)。トレーニング経過の「表示: 面談の効果」。
 *  - まとめ: 担当者別・到達時間別・種類別・カテゴリ別に、記録の件数・差が出た件数・伸びた件数・差の平均・前後の 件/h
 *  - 記録の一覧: 1件ずつの 前15h・後15h・差(行を押すとその人の詳細)
 * 数字は training.ts(summarizeEffects・effectRows)。画面では計算しない。
 */
import { useMemo, useState } from 'react';
import DataTable, { type DataTableColumn } from '@core/components/ui/DataTable';
import Panel from '@core/components/ui/Panel';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import { toDateLabel } from '@core/lib/date';
import { BUSINESS_LABELS, type BusinessType } from '@core/lib/types';
import { formatCount, NO_DATA } from '@core/utils/formatters';
import { EFFECT_HOURS, RECORD_KIND_LABELS, summarizeEffects, type EffectGroup, type EffectGroupBy, type EffectRow } from './training';
import { fmtRate, partialNote, RateDiff } from './trainingFormat';

const GROUP_OPTIONS: { key: EffectGroupBy; label: string; header: string }[] = [
  { key: 'trainer', label: '担当者別', header: '担当者' },
  { key: 'milestone', label: '到達時間別', header: '到達時間' },
  { key: 'kind', label: '種類別', header: '種類' },
  { key: 'business', label: 'カテゴリ別', header: 'カテゴリ' },
];

/** 差のセル。後が15hに届いていないときは、その時点の差に「(12.4h時点)」を添える */
export function EffectDiffCell({ row }: { row: EffectRow }) {
  const e = row.effect;
  if (e.diff !== null) {
    const note = partialNote(e);
    return (
      <>
        <RateDiff value={e.diff} />
        {note && <span className="ui-hint training-partial">{note}</span>}
      </>
    );
  }
  if (e.before && !e.after) return <span className="ui-hint">後の稼働なし</span>;
  return <span className="ui-hint">{NO_DATA}</span>;
}

export default function TrainingEffectsView({ rows, from, selectedKey, onSelect, onRenameTrainer }: { rows: EffectRow[]; from: string | null; selectedKey: string | null; onSelect: (trackKey: string) => void; onRenameTrainer: () => void }) {
  const [groupBy, setGroupBy] = useState<EffectGroupBy>('trainer');
  const groups = useMemo(() => summarizeEffects(rows, groupBy), [rows, groupBy]);
  const groupHeader = GROUP_OPTIONS.find((g) => g.key === groupBy)!.header;
  const groupLabel = (g: EffectGroup) => (groupBy === 'business' ? (BUSINESS_LABELS[g.label as BusinessType] ?? g.label) : g.label);
  // まとめの並び(名前順 → 給湯器・不用品)を、表の最初の並びにも使う
  const groupOrder = new Map(groups.map((g, i) => [g.key, i]));

  const groupColumns: DataTableColumn<EffectGroup>[] = [
    { key: 'key', header: groupHeader, width: 160, nowrap: true, sortValue: (g) => groupOrder.get(g.key) ?? 0, render: (g) => groupLabel(g) },
    ...(groupBy === 'business'
      ? []
      : [{ key: 'business', header: 'カテゴリ', width: 84, nowrap: true, sortValue: (g: EffectGroup) => g.business, render: (g: EffectGroup) => BUSINESS_LABELS[g.business] }]),
    { key: 'count', header: '記録', width: 72, numeric: true, sortValue: (g) => g.count, render: (g) => formatCount(g.count) },
    { key: 'measured', header: '比べた', width: 80, numeric: true, sortValue: (g) => g.measured, render: (g) => formatCount(g.measured) },
    { key: 'partial', header: 'うち15h未達', width: 104, numeric: true, sortValue: (g) => g.partial, render: (g) => (g.partial ? formatCount(g.partial) : <span className="ui-hint">0</span>) },
    { key: 'improved', header: '伸びた', width: 100, numeric: true, sortValue: (g) => (g.measured ? g.improved / g.measured : null), render: (g) => (g.measured ? `${formatCount(g.improved)} / ${formatCount(g.measured)}` : NO_DATA) },
    { key: 'before', header: `前${EFFECT_HOURS}h 件/h`, width: 104, numeric: true, sortValue: (g) => g.beforeRate, render: (g) => fmtRate(g.beforeRate) },
    { key: 'after', header: `後${EFFECT_HOURS}h 件/h`, width: 104, numeric: true, sortValue: (g) => g.afterRate, render: (g) => fmtRate(g.afterRate) },
    { key: 'avg', header: '差の平均', width: 96, numeric: true, sortValue: (g) => g.avgDiff, render: (g) => <RateDiff value={g.avgDiff} /> },
  ];

  const recordColumns: DataTableColumn<EffectRow>[] = [
    { key: 'date', header: '日付', width: 104, nowrap: true, sortValue: (r) => r.record.date, render: (r) => toDateLabel(r.record.date) },
    { key: 'name', header: '氏名', width: 140, nowrap: true, sortValue: (r) => r.track?.name ?? '', render: (r) => <span className="op-name">{r.track?.name ?? NO_DATA}</span> },
    { key: 'business', header: 'カテゴリ', width: 84, nowrap: true, sortValue: (r) => r.record.business, render: (r) => BUSINESS_LABELS[r.record.business] },
    { key: 'kind', header: '種類', width: 64, nowrap: true, sortValue: (r) => r.record.kind, render: (r) => RECORD_KIND_LABELS[r.record.kind] },
    { key: 'milestone', header: '到達時間', width: 80, nowrap: true, sortValue: (r) => r.record.milestone, render: (r) => (r.record.milestone ? `${r.record.milestone}h` : NO_DATA) },
    { key: 'trainer', header: '担当', width: 112, nowrap: true, sortValue: (r) => r.record.trainer, render: (r) => r.record.trainer || NO_DATA },
    { key: 'before', header: `前${EFFECT_HOURS}h 件/h`, width: 104, numeric: true, sortValue: (r) => r.effect.before?.rate ?? null, render: (r) => fmtRate(r.effect.before?.rate) },
    {
      key: 'after',
      header: `後${EFFECT_HOURS}h 件/h`,
      width: 140,
      numeric: true,
      nowrap: true,
      sortValue: (r) => r.effect.after?.rate ?? null,
      render: (r) =>
        r.effect.after ? (
          <>
            {fmtRate(r.effect.after.rate)}
            {partialNote(r.effect) && <span className="ui-hint training-partial">{partialNote(r.effect)}</span>}
          </>
        ) : (
          <span className="ui-hint">{NO_DATA}</span>
        ),
    },
    { key: 'diff', header: '差', width: 140, numeric: true, nowrap: true, sortValue: (r) => r.effect.diff, render: (r) => <EffectDiffCell row={r} /> },
  ];

  const mobileCard = (r: EffectRow) => (
    <>
      <div className="op-card-head">
        <span className="op-name">{r.track?.name ?? NO_DATA}</span>
        <span className="ui-hint">
          {toDateLabel(r.record.date)} {RECORD_KIND_LABELS[r.record.kind]}
        </span>
      </div>
      <div className="op-card-row">
        <span>前→後 件/h</span>
        <span className="is-numeric">
          {fmtRate(r.effect.before?.rate)} → {fmtRate(r.effect.after?.rate)}
          {partialNote(r.effect)}
        </span>
      </div>
      <div className="op-card-row">
        <span>差</span>
        <span className="is-numeric">
          <EffectDiffCell row={r} />
        </span>
      </div>
    </>
  );

  return (
    <>
      <Panel
        title="まとめ"
        meta={`${from ? `面談日 ${toDateLabel(from)} 以降 ・ ` : ''}記録 ${formatCount(rows.length)}件`}
        actions={
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={onRenameTrainer}>
            担当者名をまとめて変更
          </button>
        }
        toolbar={<SegmentedControl label="まとめ方" options={GROUP_OPTIONS.map(({ key, label }) => ({ key, label }))} value={groupBy} onChange={setGroupBy} />}
        note={`差 = 面談日の後の${EFFECT_HOURS}h と 前の${EFFECT_HOURS}h の 1時間あたり取得の差(面談日は含めない)。後が${EFFECT_HOURS}hに届いていない記録は、その時点の数字で比べて「(12.4h時点)」のように表示し、平均にも入れます(件数は「うち15h未達」)。`}
      >
        <DataTable caption="面談の効果のまとめ" columns={groupColumns} rows={groups} rowKey={(g) => g.key} initialSort={{ key: 'key', dir: 'asc' }} emptyMessage="面談・研修の記録がありません" compact />
      </Panel>
      <DataTable
        caption="面談・研修の記録"
        columns={recordColumns}
        rows={rows}
        rowKey={(r) => r.record.id}
        initialSort={{ key: 'date', dir: 'desc' }}
        defaultPageSize={100}
        emptyMessage="面談・研修の記録がありません(行を押して開く詳細の「面談・研修・指導を記録」から登録します。指導はここに入りません)"
        onRowClick={(r) => r.track && onSelect(r.track.key)}
        selectedKey={rows.find((r) => r.track?.key === selectedKey)?.record.id ?? null}
        mobile="cards"
        renderMobileCard={mobileCard}
      />
    </>
  );
}
